import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { inboxKeys } from "./queryKeys";
import { useActiveInboxAccounts } from "./useConversations";

type BroadcastListener = (payload: { conversationId?: string }) => void;

interface ChannelEntry {
	channel: ReturnType<typeof supabase.channel>;
	listeners: Set<BroadcastListener>;
}

// Module-level registry so the full-page inbox and the quick-peek drawer can
// both be mounted without double-subscribing to the same Supabase channel
// (also makes StrictMode double-mounts safe).
const channelRegistry = new Map<string, ChannelEntry>();

function acquireChannel(accountId: string, listener: BroadcastListener): () => void {
	let entry = channelRegistry.get(accountId);
	if (!entry) {
		const channel = supabase.channel(`unipile:${accountId}`);
		const created: ChannelEntry = { channel, listeners: new Set() };
		channel
			.on("broadcast", { event: "account_data_updated" }, (payload: { payload?: { conversationId?: string } }) => {
				created.listeners.forEach((fn) => fn(payload?.payload || {}));
			})
			.subscribe();
		channelRegistry.set(accountId, created);
		entry = created;
	}
	entry.listeners.add(listener);

	return () => {
		const current = channelRegistry.get(accountId);
		if (!current) return;
		current.listeners.delete(listener);
		if (current.listeners.size === 0) {
			supabase.removeChannel(current.channel);
			channelRegistry.delete(accountId);
		}
	};
}

/**
 * Live updates: subscribes to the per-account Unipile broadcast channels and
 * invalidates the conversation list (and the touched conversation's messages)
 * when the backend reports new data. React-query's optimistic-aware merge in
 * useMessages keeps in-flight sends intact across these refreshes.
 */
export function useInboxRealtime(enabled = true) {
	const queryClient = useQueryClient();
	const { accounts } = useActiveInboxAccounts();
	const accountIdsKey = accounts
		.map((account) => account.account_id)
		.filter(Boolean)
		.sort()
		.join(",");

	useEffect(() => {
		if (!enabled || !accountIdsKey) return;

		const handleUpdate: BroadcastListener = ({ conversationId }) => {
			queryClient.invalidateQueries({ queryKey: inboxKeys.conversations() });
			if (conversationId) {
				queryClient.invalidateQueries({ queryKey: inboxKeys.messages(conversationId) });
			}
		};

		const releases = accountIdsKey.split(",").map((accountId) => acquireChannel(accountId, handleUpdate));
		return () => releases.forEach((release) => release());
	}, [enabled, accountIdsKey, queryClient]);
}
